import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DetailHeaderSlotContext } from "./detailHeaderSlot.js";
import { useTraceCanvasMode } from "../lib/traceCanvasMode.js";
import { formatCompact, sumLayers, sumSeries } from "../lib/renderers.js";
import { buildPlotConfig } from "../lib/traceUtils.js";
import { useQuery, useCurrentRange } from "../lib/dataClient.js";
import PageHero from "./PageHero.jsx";
import ActivityChart from "./ActivityChart.jsx";
import ScrollArea from "./ScrollArea.jsx";
import TraceLogPanel from "./TraceLogPanel.jsx";
import CommandLogPanel from "./CommandLogPanel.jsx";
import SplitPage, { CONTEXT_EYEBROW_CLASS } from "./SplitPage.jsx";
import { useDockBodyHostRef } from "../lib/dockClaims.js";
import LensHost from "./lenses/LensHost.jsx";
import InfluenceTraceView from "./InfluenceTraceView.jsx";
import { appLenses } from "../lib/lenses/appLenses.js";
import DotGrid from "./DotGrid.jsx";
import GridStatus, { RevalidatingOverlay } from "./GridStatus.jsx";
import { HOVER_MATRIX } from "../lib/gridMetrics.js";
import { scopeStepLabel, useLabels, encodeScopePath } from "../lib/dimensionDefs.js";

// Overview activity layers use this fixed bucket
// resolution. 48 keeps the visible chart width-correlated to a familiar
// time-of-day grain regardless of the active range.
const OVERVIEW_BUCKET_COUNT = 48;

// Compact number for the expected baseline in the hover read — same scaling
// as ContrastPanel.formatNum so the two panels render identical legends.
function formatContextNum(v) {
  if (v >= 100) return String(Math.round(v));
  if (v >= 10) return v.toFixed(1).replace(/\.0$/, "");
  return v.toFixed(1);
}

// Verbal lift descriptor — mirrors ContrastPanel.liftPhrase so the two
// panels speak the same language on cell hover.
function contextLiftPhrase(lift) {
  if (!isFinite(lift) || lift <= 0) return null;
  if (lift >= 3) return "much more than expected";
  if (lift >= 1.5) return "above baseline";
  if (lift >= 0.8) return "about baseline";
  if (lift >= 0.3) return "below baseline";
  return "much less than expected";
}

function formatBucketLabel(start, end) {
  if (!start || !end) return "";
  const ds = new Date(start);
  const de = new Date(end);
  const fmt = (d) => `${d.getMonth() + 1}/${d.getDate()}`;
  const tm = (d) => d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const sameDay = ds.toDateString() === de.toDateString();
  return sameDay ? `${fmt(ds)} · ${tm(ds)}–${tm(de)}` : `${fmt(ds)} → ${fmt(de)}`;
}

const BREADTH_DIM_LABEL = {
  endpoints: "Endpoints",
  sessions: "Sessions",
  clusters: "Topics",
  files: "Files",
  hosts: "Hosts",
  skills: "Skills",
  agents: "Agents",
};

function truncate(text, max) {
  if (!text) return "";
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}\u2026`;
}

// Split a short phrase onto at most two lines. Prefers the natural split on
// whitespace (most cluster names are two words: "Evidence Loops"). Falls back
// to a mid-string split for single-word names that are still too long.
function wrapTwoLines(text, maxPerLine = 9) {
  if (!text) return ["", ""];
  const parts = text.trim().split(/\s+/);
  if (parts.length >= 2) {
    const first = parts[0];
    const rest = parts.slice(1).join(" ");
    return [truncate(first, maxPerLine), truncate(rest, maxPerLine)];
  }
  if (text.length <= maxPerLine) return [text, ""];
  return [text.slice(0, maxPerLine), truncate(text.slice(maxPerLine), maxPerLine)];
}

// Upper bound on baseline fetches. Scope path depth is itself bounded by
// the number of analytics dimensions (6 today), so 6 ancestor slots cover
// every reachable scope. The hook calls `useQuery` once per slot at a
// fixed index so the hook order never changes across renders; slots past
// the current ancestor count are gated `enabled: false`.
const MAX_BASELINE_SLOTS = 6;

function lensLayersOf(bundle, activityLens) {
  if (!bundle) return null;
  if (activityLens === "prompts")   return bundle.prompts?.layers   || null;
  if (activityLens === "tokens")    return bundle.tokens?.layers    || null;
  if (activityLens === "endpoints") return bundle.endpoints?.layers || null;
  if (activityLens === "activity")  return bundle.sessions?.layers  || null;
  return null;
}

function baselineLabelFor(ancestorPath) {
  if (!ancestorPath || ancestorPath.length === 0) return "Tenant";
  return scopeStepLabel(ancestorPath[ancestorPath.length - 1]);
}

// Returns `[{ key, label, scopePath, series }]` shallowest → deepest.
// The chart's toggle UI starts with every entry visible and lets the
// user hide any of them (including tenant + parent) — baselines can
// easily drown out the active scope's line in a tall ancestor stack,
// so freely clearing the lot is part of the design.
function useAncestorBaselines({ scopePath, range, bucketCount, activityLens, query = "" }) {
  const path = scopePath || [];
  const ancestorPaths = useMemo(() => {
    const out = [];
    for (let i = 0; i < path.length; i++) out.push(path.slice(0, i));
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(path)]);

  // Fixed-shape hook calls. Slots are filled left-to-right from
  // `ancestorPaths`; the rest sit disabled. Hook count is provably
  // constant (MAX_BASELINE_SLOTS) so the order rule is satisfied even
  // though the loop bound is a constant.
  const slotResults = [];
  for (let i = 0; i < MAX_BASELINE_SLOTS; i++) {
    const enabled = i < ancestorPaths.length;
    const slotPath = enabled ? ancestorPaths[i] : [];
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { data } = useQuery(
      "getOverviewActivityBundle",
      { scopePath: slotPath, bucketCount, topN: 50, range, query },
      { enabled },
    );
    slotResults.push(data);
  }

  return useMemo(() => {
    if (ancestorPaths.length === 0) return [];
    return ancestorPaths.map((p, i) => {
      const layers = lensLayersOf(slotResults[i], activityLens);
      const series = layers && layers.length ? sumLayers(layers) : null;
      return {
        key: encodeScopePath(p) || "__tenant__",
        label: baselineLabelFor(p),
        scopePath: p,
        series,
      };
    }).filter((b) => b.series && b.series.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ancestorPaths, activityLens, ...slotResults]);
}

export function CellCoOccurrencePanel({ cell, scopePath, eyebrow = "Hovered cell", pinnedFrom = null, onNavigatePath = null, activeScopePath = null }) {
  const {
    cluster,
    count,
    breadth,
    coherence,
    bucketStart,
    bucketEnd,
  } = cell;

  const cellRange = useCurrentRange();
  const { data, loading } = useQuery(
    "getCellScopeClusterMatrix",
    {
      scopePath: scopePath || [],
      clusterId: cluster?.id || null,
      bucketStart,
      bucketEnd,
      range: cellRange,
    },
    { enabled: !!cluster?.id && bucketEnd > bucketStart },
  );
  // True when we've never received data for this cell — used to show a
  // dedicated loading state instead of the empty grid (which is what the
  // user sees during the in-flight fetch). A subsequent hover into the
  // same cell (within the panel's lifetime) keeps data populated, so the
  // grid stays visible across mouse jitter — only fresh fetches trip this.
  const isInitialFetch = loading && !data;
  // True when a NEW cell's matrix is in flight but we're still showing
  // the previous cell's data (useQuery's stale-while-revalidate behavior
  // across param changes). Drives the dim+pulse overlay so the user
  // doesn't read stale numbers under a new header.
  const isRevalidating = loading && !!data;

  // Use the breadth dim the worker actually returned — it picks the axis
  // (always endpoints today) and the cell's own breadthDim can be stale
  // (e.g. "clusters" when the swimlane was scope-scoped to a cluster step).
  // Falls back to the cell's dim until the query resolves so labels don't flicker.
  const hoveredBreadthDim = data?.breadthDim || cell.breadthDim;
  const dimLabel = BREADTH_DIM_LABEL[hoveredBreadthDim] || (hoveredBreadthDim ? hoveredBreadthDim : "Scopes");

  const rawSiblings = data?.siblings || [];
  const siblingLabelSteps = useMemo(
    () => rawSiblings
      .filter((s) => s?.id && !s.isPadding)
      .map((s) => ({ dim: hoveredBreadthDim, id: s.id })),
    [rawSiblings, hoveredBreadthDim],
  );
  const siblingLabelTick = useLabels(siblingLabelSteps);
  const allSiblings = useMemo(
    () => rawSiblings.map((s) => {
      if (!s?.id || s.isPadding) return s;
      const label = scopeStepLabel({ dim: hoveredBreadthDim, id: s.id });
      return label && label !== s.id ? { ...s, name: label, label } : s;
    }),
    [rawSiblings, hoveredBreadthDim, siblingLabelTick],
  );
  const allClusters = data?.clusters || [];
  const allMatrix = data?.matrix || [];

  // Layout tokens for the hover co-occurrence grid. Sourced from the shared
  // gridMetrics module so the three data grids stay in sync. INSET_* match
  // the CSS padding used on the inner wrapper around the DotGrid (kept in
  // sync with the ContrastPanel so the two grids share the same breathing
  // room around their borders).
  const { LABEL_W, HEADER_H, PAD_R, PAD_B, TOOLBAR_H, INSET_L, INSET_R, INSET_T, INSET_B } = HOVER_MATRIX;
  // Cell size bounds — keep dots readable but pack columns in so we
  // minimise "+N more" drop-off on wider panels.
  const CELL_W_MIN = 36;
  const CELL_W_MAX = 72;
  // Row-height floor was 20 — dots would crush together when the right
  // panel was short. 26 keeps rows readable at the smallest size we'd
  // realistically render at.
  const ROW_H_MIN = 26;
  const ROW_H_MAX = 30;

  // Measure container size so the grid always fills it. The hovered cluster
  // (column 0) stays pinned; less-relevant top clusters drop off the tail
  // first. Rows and cols are padded with placeholder entries when data is
  // shorter than the fitted count so the grid structure is always visible.
  const gridContainerRef = useRef(null);
  const [gridSize, setGridSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const node = gridContainerRef.current;
    if (!node) return undefined;
    const measure = () => {
      const rect = node.getBoundingClientRect();
      const w = Math.max(0, Math.round(rect.width));
      const h = Math.max(0, Math.round(rect.height));
      setGridSize((prev) => (prev.width === w && prev.height === h ? prev : { width: w, height: h }));
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Fit as many columns of real data as possible at the densest legible
  // cell size. If that still overflows allClusters, more get dropped; if
  // there's slack, pad with blank cells so the grid stays rectangular.
  // Subtract TOOLBAR_H + INSET_* so the area we measure matches the area
  // the DotGrid actually occupies (the toolbar and the inset padding both
  // eat into gridContainerRef's measured size).
  const GRID_INSET_X = INSET_L + INSET_R;
  const GRID_INSET_Y = INSET_T + INSET_B;
  const availableGridW = Math.max(0, gridSize.width - LABEL_W - PAD_R - GRID_INSET_X);
  const availableGridH = Math.max(0, gridSize.height - HEADER_H - PAD_B - TOOLBAR_H - GRID_INSET_Y);
  const densestCols = availableGridW > 0 ? Math.max(1, Math.floor(availableGridW / CELL_W_MIN)) : 1;
  const densestRows = availableGridH > 0 ? Math.max(1, Math.floor(availableGridH / ROW_H_MIN)) : 1;
  const targetCols = Math.max(1, Math.min(densestCols, allClusters.length || densestCols));
  const targetRows = Math.max(1, Math.min(densestRows, allSiblings.length || densestRows));
  const CELL_W = availableGridW > 0 && targetCols > 0
    ? Math.max(CELL_W_MIN, Math.min(CELL_W_MAX, Math.floor(availableGridW / targetCols)))
    : CELL_W_MIN;
  const ROW_H = availableGridH > 0 && targetRows > 0
    ? Math.max(ROW_H_MIN, Math.min(ROW_H_MAX, Math.floor(availableGridH / targetRows)))
    : ROW_H_MIN;
  const fittedCols = availableGridW > 0 ? Math.max(1, Math.floor(availableGridW / CELL_W)) : 1;
  const fittedRows = availableGridH > 0 ? Math.max(1, Math.floor(availableGridH / ROW_H)) : 1;
  const colCount = Math.min(allClusters.length, fittedCols);
  const dataClusters = allClusters.slice(0, colCount);
  const droppedCols = allClusters.length - colCount;
  const clusters = [...dataClusters];
  while (clusters.length < fittedCols) {
    clusters.push({ id: `__pad_c_${clusters.length}`, name: "", isPadding: true });
  }
  const siblings = [...allSiblings.slice(0, fittedRows)];
  while (siblings.length < fittedRows) {
    siblings.push({ id: `__pad_r_${siblings.length}`, name: "", isPadding: true });
  }
  const matrix = allMatrix.map((row) => row.slice(0, colCount));
  let maxCell = 0;
  for (const row of matrix) for (const v of row) if (v > maxCell) maxCell = v;

  // Hover feedback for the grid (mirrors ContrastPanel): updates the header
  // strip and drives a soft column wash so the user sees what they're pointing
  // at. Skips padding slots so blank cells don't flash.
  const [hover, setHover] = useState(null);
  const handleHover = (hit) => {
    if (!hit) { setHover(null); return; }
    const row = siblings[hit.ri];
    const col = clusters[hit.ci];
    if ((hit.type === "row" || hit.type === "cell") && row?.isPadding) { setHover(null); return; }
    if ((hit.type === "col" || hit.type === "cell") && col?.isPadding) { setHover(null); return; }
    setHover({ kind: hit.type, ri: hit.ri, ci: hit.ci });
  };

  const maxRadius = Math.min(CELL_W, ROW_H) * 0.46;
  const minRadius = Math.max(2.6, maxRadius * 0.38);

  // Chi-squared independence model: shade = expected, dot = surprise.
  // Same visual grammar as the ContrastPanel (shade=baseline, dot=deviation)
  // so the context panel reads identically when the user hovers a swimlane
  // cell. Computed JS-side from the raw observed matrix because
  // getCellScopeClusterMatrix only returns counts — small, sibling × cluster,
  // so even a recompute on every hover is sub-ms.
  const { expectedMatrix, surpriseMatrix, maxExpected, minExpected, maxSurprise } = useMemo(() => {
    const rowTotals = new Array(matrix.length).fill(0);
    const colTotals = new Array(clusters.length).fill(0);
    let grand = 0;
    for (let i = 0; i < matrix.length; i++) {
      const row = matrix[i] || [];
      for (let j = 0; j < clusters.length; j++) {
        const v = row[j] || 0;
        rowTotals[i] += v;
        colTotals[j] += v;
        grand += v;
      }
    }
    const expected = matrix.map(() => new Array(clusters.length).fill(0));
    const surprise = matrix.map(() => new Array(clusters.length).fill(0));
    let maxE = 0, minE = Infinity, maxS = 0;
    if (grand > 0) {
      for (let i = 0; i < matrix.length; i++) {
        for (let j = 0; j < clusters.length; j++) {
          const e = (rowTotals[i] * colTotals[j]) / grand;
          const s = Math.max(0, (matrix[i]?.[j] || 0) - e);
          expected[i][j] = e;
          surprise[i][j] = s;
          if (e > maxE) maxE = e;
          if (e > 0 && e < minE) minE = e;
          if (s > maxS) maxS = s;
        }
      }
    }
    if (!Number.isFinite(minE)) minE = 0;
    return {
      expectedMatrix: expected,
      surpriseMatrix: surprise,
      maxExpected: maxE,
      minExpected: minE,
      maxSurprise: maxS,
    };
  }, [matrix, clusters.length]);
  const expectedFloor = minExpected || 0;
  const expectedRange = Math.max(1e-6, (maxExpected || 1) - expectedFloor);
  const surpriseMax = maxSurprise || 1;
  const SHADE_ALPHA_MAX = 0.48;
  // Two highlight treatments, nothing more:
  //   source = the scope the panel was pinned from (stable; green fill)
  //   active = the current app scope the user has navigated to (outline)
  // We do NOT track the hovered cluster — that shifts on every mouse move and
  // made the grid feel inconsistent. The panel header already names the cell.
  // For source we take the shallowest matching step in the pinned scope; for
  // active we take the *deepest* — the scope path can stack multiple steps of
  // the same dim (e.g. clusters:A / clusters:B), and the user's "current"
  // position is the last one they drilled into.
  const indexForStep = (scope, dim, axis, mode = "first") => {
    if (!scope || !dim) return -1;
    const step = mode === "last" ? scope.findLast((s) => s.dim === dim) : scope.find((s) => s.dim === dim);
    return step ? axis.findIndex((a) => a.id === step.id) : -1;
  };
  const sourceRowIndex = indexForStep(scopePath, hoveredBreadthDim, siblings);
  // In pinned mode the pinned cell's own cluster is part of "source" — that's
  // the column the user clicked to create the pin. In live-hover mode we
  // deliberately skip this because the hovered cell changes with every mouse
  // move and tracking it made the highlights feel bouncy.
  const scopeCol = indexForStep(scopePath, "clusters", clusters);
  const pinnedCol = pinnedFrom && cluster?.id
    ? clusters.findIndex((c) => c.id === cluster.id)
    : -1;
  const sourceColIndex = scopeCol >= 0 ? scopeCol : pinnedCol;
  const rawActiveRow = indexForStep(activeScopePath, hoveredBreadthDim, siblings, "last");
  const rawActiveCol = indexForStep(activeScopePath, "clusters", clusters, "last");
  // Suppress active highlight when it would land on the same row/col as source.
  const activeRowIndex = rawActiveRow === sourceRowIndex ? -1 : rawActiveRow;
  const activeColIndex = rawActiveCol === sourceColIndex ? -1 : rawActiveCol;

  const cellFn = (si, ci) => {
    const expected = expectedMatrix[si]?.[ci] || 0;
    const surprise = surpriseMatrix[si]?.[ci] || 0;
    let shade = null;
    if (expected > 0) {
      const norm = Math.max(0, (expected - expectedFloor) / expectedRange);
      shade = { opacity: Math.sqrt(norm) * SHADE_ALPHA_MAX };
    }
    if (surprise > 0) {
      const norm = Math.sqrt(surprise / surpriseMax);
      const r = minRadius + norm * (maxRadius - minRadius);
      const opacity = Math.min(1, 0.62 + norm * 0.38);
      return { shade, dot: { r, opacity } };
    }
    return { shade, placeholder: { r: Math.max(1.1, minRadius * 0.6), opacity: 0.16 } };
  };

  // Cell tooltip mirrors the contrast panel — same readout shape, same
  // verbal grammar, so the user reads both panels with one mental model.
  const cellTooltipFn = (si, ci, row, col) => {
    if (!row || !col) return null;
    const observed = matrix[si]?.[ci] || 0;
    const expected = expectedMatrix[si]?.[ci] || 0;
    if (observed === 0 && expected === 0) return null;
    const lift = expected > 0 ? observed / expected : 0;
    const rowLabel = row.name || row.label || row.id;
    const colLabel = col.name || col.label || col.id;
    const phrase = observed > 0 && expected > 0 ? contextLiftPhrase(lift) : null;
    return (
      <div className="flex flex-col gap-[4px]">
        <div className="flex flex-col gap-[1px] font-medium text-[color:var(--ink)]">
          <div className="flex min-w-0 items-baseline gap-[6px]">
            <span className="shrink-0 text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted-soft)]">row</span>
            <span className="min-w-0 truncate">{rowLabel}</span>
          </div>
          <div className="flex min-w-0 items-baseline gap-[6px]">
            <span className="shrink-0 text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted-soft)]">col</span>
            <span className="min-w-0 truncate">{colLabel}</span>
          </div>
        </div>
        {observed > 0 && expected > 0 ? (
          <>
            <div className={lift >= 1.5 ? "text-[color:var(--ink)]" : "text-[color:var(--muted)]"}>
              {phrase ? `${phrase} · ` : ""}{lift.toFixed(1)}× the baseline rate
            </div>
            <div className="text-[color:var(--muted)]">
              {observed} traces here, ~{formatContextNum(expected)} baseline
            </div>
            <div className="text-[10px] text-[color:var(--muted-soft)] leading-[1.3]">
              Baseline = this endpoint's traces × this topic's share of the bucket.
              It's the count we'd see if this endpoint didn't favor any topic.
            </div>
          </>
        ) : observed > 0 ? (
          <div className="text-[color:var(--muted)]">
            {observed} traces here · no baseline available
          </div>
        ) : (
          <div className="text-[color:var(--muted)]">
            No traces here · baseline would be ~{formatContextNum(expected)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-[10px] overflow-hidden px-[2px] py-[10px]">
      <div className="grid gap-[6px] border border-[color:var(--line)] bg-[color:var(--panel-soft)] px-[11px] py-[9px] text-[11px] leading-[1.24] text-[color:var(--muted)]">
        <div className="flex items-center justify-between gap-[8px]">
          <div className="text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted-soft)]">{eyebrow}</div>
          {pinnedFrom?.isCurrent && pinnedFrom?.onUnpin ? (
            <button
              type="button"
              onClick={pinnedFrom.onUnpin}
              title="Remove pinned cell"
              className="inline-flex items-center gap-[4px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[6px] py-[2px] text-[10px] uppercase tracking-[0.08em] leading-[1.1] text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)]"
            >
              <svg viewBox="0 0 12 12" className="h-[10px] w-[10px]" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 3l6 6" />
                <path d="M9 3l-6 6" />
              </svg>
              <span>Unpin</span>
            </button>
          ) : pinnedFrom?.label ? (
            <button
              type="button"
              onClick={pinnedFrom.onNavigate}
              disabled={!pinnedFrom.onNavigate}
              title={`Return to ${pinnedFrom.label}`}
              className="inline-flex items-center gap-[4px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[6px] py-[2px] text-[10px] leading-[1.1] text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)] disabled:cursor-default disabled:opacity-60"
            >
              <svg viewBox="0 0 12 12" className="h-[10px] w-[10px]" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M7 2L3 6l4 4" />
                <path d="M3 6h6" />
              </svg>
              <span className="truncate max-w-[140px]">{pinnedFrom.label}</span>
            </button>
          ) : null}
        </div>
        <div className="text-[14px] leading-[1.12] font-medium text-[color:var(--ink)] tracking-[-0.02em]">{cluster?.name}</div>
        <div>{formatBucketLabel(bucketStart, bucketEnd)}</div>
        <div className="flex flex-wrap gap-x-[14px] gap-y-[2px]">
          <span>{count} traces in scope</span>
          <span>{breadth} sibling {dimLabel.toLowerCase()}</span>
          <span>{Math.round((coherence || 0) * 100)}% coherent with siblings</span>
        </div>
      </div>
      <div ref={gridContainerRef} className="relative flex min-h-0 flex-1 flex-col overflow-hidden border border-[color:var(--line)] bg-[color:var(--bg)]">
        <div
          className="flex flex-col border-b border-[color:var(--line-soft)] text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted)]"
          style={{ height: TOOLBAR_H }}
        >
          <div className="flex h-[21px] min-h-[21px] items-center gap-[8px] px-[11px]">
            <span className="shrink-0 text-[color:var(--muted-soft)]">rows:</span>
            <span className="min-w-0 truncate text-[color:var(--ink)]">{dimLabel.toLowerCase()}</span>
            <span className="shrink-0 text-[color:var(--muted-soft)]">at this bucket</span>
          </div>
          <div className="flex h-[21px] min-h-[21px] items-center gap-[8px] border-t border-[color:var(--line-soft)] px-[11px]">
            <span className="shrink-0 text-[color:var(--muted-soft)]" aria-hidden="true">×</span>
            <span className="shrink-0 text-[color:var(--muted-soft)]">cols:</span>
            <span className="min-w-0 truncate text-[color:var(--ink)]">topics</span>
            {droppedCols > 0 ? (
              <span className="shrink-0 normal-case tracking-normal text-[color:var(--muted-soft)]">+{droppedCols} more</span>
            ) : null}
          </div>
          <div className="flex h-[22px] min-h-[22px] flex-1 items-center gap-[12px] overflow-hidden border-t border-[color:var(--line-soft)] px-[11px]">
            <span className="flex min-w-0 flex-1 items-center overflow-hidden">
            {(() => {
              const legend = (
                <span className="flex items-center justify-end gap-[10px] normal-case tracking-normal text-[color:var(--muted-soft)]">
                  <span className="inline-flex items-center gap-[4px]">
                    <span className="h-[8px] w-[8px] rounded-[1px] bg-[color:var(--green)]" style={{ opacity: 0.35 }} />
                    baseline rate
                  </span>
                  <span className="inline-flex items-center gap-[4px]">
                    <span className="h-[7px] w-[7px] rounded-full bg-[color:var(--green)]" />
                    above baseline
                  </span>
                </span>
              );
              if (!hover) {
                return (
                  <span className="flex items-center justify-between gap-[12px] w-full normal-case tracking-normal">
                    <span className="text-[color:var(--muted-soft)]">hover a cell to inspect</span>
                    {legend}
                  </span>
                );
              }
              if (hover.kind === "row") {
                const r = siblings[hover.ri];
                if (!r || r.isPadding) return null;
                return (
                  <span className="flex items-center justify-between gap-[12px] w-full normal-case tracking-normal">
                    <span className="min-w-0 truncate text-[color:var(--ink)]">
                      <span className="text-[color:var(--muted-soft)]">row</span> {r.name || r.label || r.id}
                    </span>
                    {legend}
                  </span>
                );
              }
              if (hover.kind === "col") {
                const c = clusters[hover.ci];
                if (!c || c.isPadding) return null;
                return (
                  <span className="flex items-center justify-between gap-[12px] w-full normal-case tracking-normal">
                    <span className="min-w-0 truncate text-[color:var(--ink)]">
                      <span className="text-[color:var(--muted-soft)]">col</span> {c.name || c.label || c.id}
                    </span>
                    {legend}
                  </span>
                );
              }
              const r = siblings[hover.ri];
              const c = clusters[hover.ci];
              if (!r || !c || r.isPadding || c.isPadding) return null;
              const observed = matrix[hover.ri]?.[hover.ci] || 0;
              const expected = expectedMatrix[hover.ri]?.[hover.ci] || 0;
              const lift = expected > 0 ? observed / expected : 0;
              const phrase = observed > 0 && expected > 0 ? contextLiftPhrase(lift) : null;
              return (
                <span className="flex items-center justify-between gap-[12px] w-full normal-case tracking-normal">
                  <span className="min-w-0 truncate text-[color:var(--ink)]">
                    {r.name || r.id} <span className="text-[color:var(--muted-soft)]">×</span> {c.name || c.id}
                    <span className="text-[color:var(--muted-soft)]"> · </span>
                    {observed} traces
                  {observed > 0 && expected > 0 ? (
                    <>
                      <span className="text-[color:var(--muted-soft)]"> · </span>
                      <span className={lift >= 1.5 ? "text-[color:var(--ink)]" : "text-[color:var(--muted)]"}>
                        {lift.toFixed(1)}× baseline of ~{formatContextNum(expected)}
                      </span>
                    </>
                  ) : observed > 0 ? (
                    <>
                      <span className="text-[color:var(--muted-soft)]"> · </span>
                      <span className="text-[color:var(--muted)]">no baseline for this cell</span>
                    </>
                  ) : null}
                  </span>
                  {phrase ? (
                    <span className="shrink-0 text-[color:var(--muted-soft)]">{phrase}</span>
                  ) : (
                    legend
                  )}
                </span>
              );
            })()}
            </span>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-hidden">
          {isInitialFetch ? (
            <GridStatus state="loading" label="Fetching cell context…" />
          ) : allSiblings.length === 0 || allClusters.length === 0 ? (
            <GridStatus state="empty" label="No co-occurring activity at this cell." />
          ) : (
            // Inset matches the contrast panel: row labels and column
            // headers don't press against the panel's own border.
            <div className="h-full w-full pl-[10px] pr-[6px] pt-[8px] pb-[6px]">
            <DotGrid
              rows={siblings}
              cols={clusters}
              cellW={CELL_W}
              rowH={ROW_H}
              labelW={LABEL_W}
              headerH={HEADER_H}
              colHeaderLines={2}
              rowLabelAlign="right"
              cellFn={cellFn}
              cellTooltipFn={cellTooltipFn}
              selfRowIndex={sourceRowIndex}
              selfColIndex={sourceColIndex}
              activeRowIndex={activeRowIndex}
              activeColIndex={activeColIndex}
              hoveredRowIndex={hover && siblings[hover.ri] && !siblings[hover.ri].isPadding ? hover.ri : -1}
              hoveredColIndex={hover && clusters[hover.ci] && !clusters[hover.ci].isPadding ? hover.ci : -1}
              onHover={handleHover}
              onClick={onNavigatePath ? (hit) => {
                if (!hit) return;
                const row = siblings[hit.ri];
                const col = clusters[hit.ci];
                // Skip padding slots — they carry no real id.
                if ((hit.type === "row" || hit.type === "cell") && row?.isPadding) return;
                if ((hit.type === "col" || hit.type === "cell") && col?.isPadding) return;
                // Replace any existing step of the same dim rather than stacking:
                // the panel's rows are sibling endpoints and cols are clusters,
                // so clicking one means "switch to this value in that dim".
                // Stacking (e.g. endpoints:A / endpoints:B) is an empty AND.
                const replace = (base, dim, id) => [...base.filter((s) => s.dim !== dim), { dim, id }];
                const base = scopePath || [];
                if (hit.type === "row") {
                  onNavigatePath(replace(base, hoveredBreadthDim, row.id));
                } else if (hit.type === "col") {
                  onNavigatePath(replace(base, "clusters", col.id));
                } else if (hit.type === "cell") {
                  onNavigatePath(replace(replace(base, hoveredBreadthDim, row.id), "clusters", col.id));
                }
              } : undefined}
            />
            </div>
          )}
        </div>
        <RevalidatingOverlay active={isRevalidating} label="Refreshing cell context…" />
      </div>
    </div>
  );
}

/**
 * ScopedView — the shared page structure used by both Overview and Endpoint.
 *
 * It renders: SplitPage → PageHero + ActivityChart + TraceLogPanel | detail panel
 *
 * The right panel handles swimlane/flow switching internally.
 * Pages provide `detailContent` for flow-mode content, or `rightContent`
 * for full control (legacy).
 */
// The right panel header's byline row: the page's byline, unless a hovered
// Contrast cell has taken the header for its co-occurrence reading, whose
// title the byline would misdescribe. Named and exported so the gate is
// testable on its own; mounting the view means mounting its data layer.
export function detailHeaderByline(contextByline, showCellCoOccurrence) {
  return contextByline && !showCellCoOccurrence ? contextByline : null;
}

export default function ScopedView({
  hero,
  traces,
  selectedCluster = null,
  selectedTrace = null,
  clusters = [],
  range,
  activityLens,
  // Dimension-level lens focus (e.g. "signals" → alert console). Threaded
  // into the LensHost target; null means resolve value/scope lenses.
  focusDim = null,
  // Active lens id within the resolved set (set from the sidebar lens links);
  // null = the set's default. Threaded into the LensHost target.
  view = null,
  // Controlled log-tab selection — round-trips through ?log= in the URL.
  // Optional; if the caller doesn't pass it we fall back to internal
  // useState (legacy behavior for pages that haven't been hoisted yet).
  logTab: logTabProp,
  onLogTabChange,
  // RBAC sensitive-content gates, keyed on read.sensitive by the host: the
  // Trace log requires ai_sessions read.sensitive and the Execution log
  // requires events read.sensitive. A log whose content the caller can't read
  // is hidden (not shown empty). Default open so non-RBAC embeds are unaffected.
  canTracePrompts = true,
  promptsWithheldHere = false,
  canRawCommands = true,
  theme,
  palette,
  onRangeChange,
  onActivityLensChange,
  focusHeader,
  resetKey,
  traceLog = {},
  // The trace log's session grain (see ScopeOverviewBody): rows from
  // getSessionLog and their own loading / denial / sort / paging.
  sessionLog = {},
  sessions = [],
  commands = [],
  commandLog = {},
  rightHeader,
  // Full-height mode rail pinned to the right edge — a render prop the
  // page supplies (ScopedPage builds it from panelMode); passed straight
  // through to SplitPage, which mounts it as a flex sibling of the
  // column area and injects the collapse state it owns.
  rail,
  // Right-column panel mode ("context" | "chat"), owned by ScopedPage.
  // In "chat" the right-column body is the docked chat host.
  panelMode = "context",
  // Whether this Explore surface is the currently-visible dock host. When it
  // flips back to visible (e.g. after the Canvases home grabbed the shared
  // dock body host for its in-column chat), we re-assert our host node so the
  // docked chat re-portals here.
  dockVisible = true,
  // Reports the right detail panel's collapse state up to ScopedPage (gates
  // the chat dock — collapsed → ⌘K opens the overlay).
  onDetailCollapsedChange,
  // Flow-mode content render function — receives { palette, traceStats }
  detailContent,
  // Title shown in the right panel header.
  contextTitle = "Contrast",
  // Eyebrow above the right-panel title; pages override it when the
  // active tab isn't scope context (e.g. "Signal definition").
  contextEyebrow = "Context",
  // Optional byline under the title (a trace's endpoint, login, tooling). It
  // takes the header's full width on its own line: beside the title it fought
  // the nav buttons for room and clipped after a few words.
  contextByline = null,
  // Lens-opened detail handler. When the page provides one, lens
  // ctx.onOpenDetail routes there (ScopedPage opens a real tab in the
  // detail strip); without it, fall back to the local takeover overlay.
  // The takeover replaced the panel body while the trace tabs stayed
  // visible above content they didn't own — "view definition" looked
  // like it hijacked whichever tab was active (O-1781).
  onOpenLensDetail = null,
  // Re-orient the right panel to the scope's default Context view (the tab
  // that already renders the signal definition). The per-signal lens's "View
  // definition" routes here instead of stacking a duplicate definition tab.
  onShowSignalDefinition = null,
  // Optional trace-navigation controls in the right panel header.
  // Shape: { onPrev, onNext, hasPrev, hasNext, position } where position
  // is "Trace 4 of 28" style label.
  traceNav = null,
  // How to group cluster traces in swimlane hover mode (default: by endpointName)
  clusterTraceGroupBy,
  scopePath,
  onClickCell,
  onNavigatePath,
  // Legacy: full right panel render function (used if detailContent is not provided)
  rightContent,
  // Overview pivot: when set, ONLY the activity surface re-scopes to this
  // path (and goes full-screen) — the hero, trace log, detail, scope columns
  // and tab strip all stay on `scopePath`. Lets the pivot "take over" the
  // constant-size surface to show another scope without swapping tabs or
  // resetting any surrounding content.
  surfaceScopeOverride = null,
  // Influence tracing — when set, the main column swaps from the lens
  // host to the influence-trace surface for this { key, trace, turn,
  // turnIndex } (the trace stays in the right column). The tab itself is
  // owned by App's explorer strip; this is just the resolved descriptor.
  influenceDescriptor = null,
  onOpenTrace,
  onInfluenceScopeNavigate,
  // Query-language refinement (page-level). Folds into the activity-surface
  // queries and scoped kernels as more PROMPT-rooted scope.
  query = "",
  // Writes the current scope level's steer (see ScopedPage). Null when
  // steering is unavailable — consumers hide their steer affordances.
  onSteerScope = null,
}) {
  // The activity surface (chart + swimlane + baselines) renders against the
  // override when pivoting; everything else stays on the page scope.
  const surfaceScope = surfaceScopeOverride || scopePath;

  // Prototype dock: the right panel hosts either Context or Chat. When
  // flipped to Chat we swap its body for a host node and publish it via
  // dockStore so the single ChatConsole instance portals in. The
  // callback ref fires with the node on mount and null on unmount (i.e.
  // when flipping back to Context), which is exactly the dock lifecycle.
  // Right-column body host for the docked chat. panelMode is owned by
  // ScopedPage (it also owns the rail where the mode switch lives) and
  // passed in; here we just publish the body node when in Chat mode.
  // The callback ref fires with the node on mount and null on unmount,
  // which is exactly the dock lifecycle as panelMode flips.
  // The dock body host is a single global slot with last-writer-wins, and
  // this view stays mounted (hidden + inert) under the dashboard overlay.
  // The claim/release discipline (claim only while visible, release only
  // if still the owner) lives — and is tested — in dockClaims.js.
  const { ref: dockHostRef, reclaim: reclaimDockHost } = useDockBodyHostRef(dockVisible);
  // Another visible surface (the Canvases home) can claim the slot while
  // we're hidden; reclaim it when we become visible again in chat mode so
  // the docked chat returns to this column.
  useEffect(() => {
    if (dockVisible && panelMode === "chat") reclaimDockHost();
  }, [dockVisible, panelMode, reclaimDockHost]);
  // Title for the surface — follows the override so the full-screen pivot
  // doesn't read e.g. "Claude activity" while showing the overview chart.
  const surfaceTitle = surfaceScopeOverride
    ? (surfaceScopeOverride.length
        ? `${scopeStepLabel(surfaceScopeOverride[surfaceScopeOverride.length - 1])} activity`
        : "Overview activity")
    : null;
  // One API call returns all chart lenses. Each lens still ranks its top-K
  // agents independently by metric, but the backend request shares the same
  // scope, bucket grid, and grouping.
  const bucketCount = OVERVIEW_BUCKET_COUNT;
  const { data: activityBundle, loading: activityLoading } = useQuery("getOverviewActivityBundle", {
    scopePath: surfaceScope, bucketCount, topN: 6, range, query,
  });
  const activityData = activityBundle?.sessions;
  const promptData = activityBundle?.prompts;
  const endpointsData = activityBundle?.endpoints;
  const tokensData = activityBundle?.tokens;
  const windowItem = useMemo(() => ({
    activityLayers:  activityData?.layers  ?? [],
    promptLayers:    promptData?.layers    ?? [],
    endpointsLayers: endpointsData?.layers ?? [],
    tokenLayers:     tokensData?.layers    ?? [],
    tokenCost:       tokensData?.cost      ?? null,
    activity:        activityData?.total   ?? 0,
    prompts:         promptData?.total     ?? 0,
    endpointsTotal:  endpointsData?.total  ?? 0,
    tokens:          tokensData?.total     ?? 0,
  }), [activityData, promptData, endpointsData, tokensData]);
  const plotConfig = buildPlotConfig(activityLens, windowItem, palette);

  // Ancestor-scope baselines. For a scopePath of depth N the chart shows
  // every prefix shallower than the active scope — tenant ([]) plus every
  // intermediate ancestor — so a 3-level scope can compare its line
  // against grandparent / parent / tenant all at once. Tenant + parent
  // are marked `locked` so the toggle UI can't drop below that floor.
  //
  // Use a large topN for ancestors. The main chart shows top-6 agents at
  // the active scope, but a baseline needs to approximate the ancestor's
  // TRUE total — including agents in the long tail that dominate at a
  // specific scope. Without this, the baseline can dip below the scoped
  // line, which is impossible by definition (scope ⊆ ancestor).
  const baselines = useAncestorBaselines({
    scopePath: surfaceScope,
    range,
    bucketCount,
    activityLens,
    query,
  });

  // Log tab: sessions (the trace log's default grain) vs requests vs commands.
  // Controlled when the caller passes `logTab` + `onLogTabChange` (App.jsx
  // does this so the value lands in ?log=); uncontrolled fallback keeps
  // legacy embeds working.
  const [logTabInternal, setLogTabInternal] = useState("sessions");
  // Where the compact trace header lets the panel put its reading switcher.
  const [headerSlot, setHeaderSlot] = useState(null);
  // On the trace canvas the header line has nothing to say: its byline is one
  // trace's facts and its switcher is one trace's readings, and the canvas
  // holds many traces, each carrying its own facts on its card.
  const traceCanvasOn = useTraceCanvasMode();
  const logTab = logTabProp !== undefined ? logTabProp : logTabInternal;
  const setLogTab = onLogTabChange || setLogTabInternal;

  // Hover state with directionality to prevent feedback loops
  const [hoveredCluster, setHoveredCluster] = useState(null);
  const [hoverSource, setHoverSource] = useState(null);
  const [hoveredCell, setHoveredCell] = useState(null);

  const handleHoverClusterLeft = useCallback((cluster) => {
    setHoveredCluster(cluster);
    setHoverSource(cluster ? "left" : null);
  }, []);

  const handleHoverClusterRight = useCallback((cluster) => {
    setHoveredCluster(cluster);
    setHoverSource(cluster ? "right" : null);
  }, []);

  const handleHoverCellLeft = useCallback((cell) => {
    setHoveredCell(cell);
  }, []);

  const traceStats = useMemo(() => {
    const uniqueSessionCount = new Set(traces.map((t) => t.sessionId)).size;
    // O-1805: per-trace token totals are gone (per-prompt usage is no longer
    // joinable, so trace rows carry 0). Use the scope-level token total the
    // activity bundle already computed instead of reducing the now-zero
    // per-row tokens. `tokens.total` is the per-bucket token *series* (the same
    // array the Token Volume graph plots), so sum it to a scalar — handing the
    // array straight to formatCompact coerced it to NaN (O-1843).
    const totalTokens = sumSeries(tokensData?.total);
    // Scope-level per-bucket sums + coverage (input / output / work / cache /
    // reasoning) the summary bar renders as the in·out·total split with the rest on
    // hover. Computed once in the bundle (sumTokenBreakdown); null until loaded.
    const tokenBreakdown = tokensData?.breakdown ?? null;
    // Estimated USD cost for the scope (priced per provider+model, summed in
    // the bundle). null until loaded / when nothing in scope is catalogued.
    const tokenCost = tokensData?.cost ?? null;
    return { totalTokens, tokenBreakdown, tokenCost, uniqueSessionCount };
  }, [traces, tokensData]);

  // Lens-driven detail: a lens (e.g. the skill content-compare) can open
  // arbitrary content in the right panel via ctx.onOpenDetail({ eyebrow, title,
  // body, detailKey }). Cleared on navigation so it never outlives its lens.
  const [lensDetail, setLensDetail] = useState(null);
  const scopeKey = encodeScopePath(scopePath);
  useEffect(() => { setLensDetail(null); }, [scopeKey, focusDim, view]);

  // The right panel render function — either uses detailContent (unified) or rightContent (legacy)
  const resolvedRightContent = detailContent
    ? (props) => {
        const { expanded, onToggleExpand } = props;
        const showCellCoOccurrence = !!hoveredCell && !!hoveredCell.cluster;
        const hoveredDim = hoveredCell?.breadthDim;
        const hoveredDimLabel = BREADTH_DIM_LABEL[hoveredDim] || hoveredDim || "scopes";
        // Chat mode: the chat owns the right-column body — its session
        // tabs live on the rail above (rendered by ScopedPage from the
        // published chatBar). Render only the portal host here;
        // ChatConsole portals its body (sans tab strip) into it.
        if (panelMode === "chat") {
          return <div ref={dockHostRef} className="h-full min-h-0 min-w-0 bg-[color:var(--bg)]" />;
        }

        // Lens-opened detail (e.g. a skill version body) takes over the right
        // panel until closed or navigated away.
        if (lensDetail) {
          return (
            <div className="grid h-full min-h-0 min-w-0 overflow-hidden bg-[color:var(--bg)]" style={{ gridTemplateRows: "auto minmax(0,1fr)" }}>
              <div className="overflow-hidden border-b border-[color:var(--line-soft)]">
                <div className="grid gap-0 pt-3 pb-2 [grid-template-columns:minmax(0,1fr)_auto] items-start">
                  <div className="min-w-0">
                    <div className={CONTEXT_EYEBROW_CLASS}>{lensDetail.eyebrow || "Detail"}</div>
                    <h2 className="m-0 mt-[3px] truncate text-[15px] leading-[1.06] font-medium tracking-[-0.04em]">{lensDetail.title}</h2>
                  </div>
                  <div className="flex items-stretch gap-[4px]">
                    {onToggleExpand ? (
                      <button type="button" onClick={onToggleExpand} aria-label={expanded ? "Collapse" : "Expand"} className="grid h-[24px] w-[24px] place-items-center border border-[color:var(--line)] bg-[color:var(--control-bg)] p-0 text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)] [&_svg]:block [&_svg]:h-3 [&_svg]:w-3">
                        {expanded ? (
                          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12l8-8" /><path d="M12 12L4 4" /></svg>
                        ) : (
                          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"><path d="M10 2h4v4" /><path d="M6 14H2v-4" /><path d="M14 2l-4 4" /><path d="M2 14l4-4" /></svg>
                        )}
                      </button>
                    ) : null}
                    <button type="button" onClick={() => setLensDetail(null)} aria-label="Close" className="grid h-[24px] w-[24px] place-items-center border border-[color:var(--line)] bg-[color:var(--control-bg)] p-0 text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)] [&_svg]:block [&_svg]:h-3 [&_svg]:w-3">
                      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4l8 8M12 4l-8 8" /></svg>
                    </button>
                  </div>
                </div>
              </div>
              {lensDetail.content ? (
                // Rich detail (e.g. the signal definition panel) brings its own
                // scroll container; mount it directly so nested ScrollAreas
                // don't fight over the wheel.
                <div className="min-h-0 min-w-0 overflow-hidden px-[2px]">{lensDetail.content}</div>
              ) : (
                <ScrollArea className="min-h-0 h-full" contentClassName="px-[2px] py-[8px]">
                  <div className="whitespace-pre-wrap break-words font-mono text-[12px] leading-[1.55] text-[color:var(--ink)]">{lensDetail.body}</div>
                </ScrollArea>
              )}
            </div>
          );
        }

        const headerTitle = showCellCoOccurrence
          ? `${hoveredCell.cluster.name} · across ${hoveredDimLabel.toLowerCase()}`
          : contextTitle;
        const headerByline = detailHeaderByline(contextByline, showCellCoOccurrence);

        const body = showCellCoOccurrence
          ? <CellCoOccurrencePanel cell={hoveredCell} scopePath={scopePath} activeScopePath={scopePath} onNavigatePath={onNavigatePath} />
          : detailContent({ ...props, palette, traceStats });
        // Empty context (e.g. collapsing to nothing after the last detail tab
        // closed) — render a blank panel with no header, so nothing flashes as
        // the column fades out.
        if (body == null) {
          return <div className="h-full min-h-0 min-w-0 bg-[color:var(--bg)]" />;
        }

        const controls = (
          <div className="flex items-stretch gap-[4px]">
          {traceNav ? (
            <span className="inline-flex h-[24px] items-stretch">
              <button
                type="button"
                onClick={traceNav.onPrev}
                disabled={!traceNav.hasPrev}
                aria-label="Previous request"
                title="Previous request"
                className="grid h-[24px] w-[24px] place-items-center border border-[color:var(--line)] bg-[color:var(--control-bg)] p-0 text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)] disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer [&_svg]:block [&_svg]:h-3 [&_svg]:w-3"
              >
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M10 4 L4 8 L10 12" />
                </svg>
              </button>
              {traceNav.position ? (
                <span className="grid h-[24px] place-items-center border-y border-r border-[color:var(--line)] bg-[color:var(--control-bg)] px-[8px] text-[10px] leading-[1] tracking-[0.04em] uppercase tabular-nums text-[color:var(--muted)]">
                  {traceNav.position}
                </span>
              ) : null}
              <button
                type="button"
                onClick={traceNav.onNext}
                disabled={!traceNav.hasNext}
                aria-label="Next request"
                title="Next request"
                className="grid h-[24px] w-[24px] place-items-center border border-l-0 border-[color:var(--line)] bg-[color:var(--control-bg)] p-0 text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)] disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer [&_svg]:block [&_svg]:h-3 [&_svg]:w-3"
              >
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M6 4 L12 8 L6 12" />
                </svg>
              </button>
            </span>
          ) : null}
          {onToggleExpand ? (
            <button
              type="button"
              onClick={onToggleExpand}
              className="grid h-[24px] w-[24px] place-items-center border border-[color:var(--line)] bg-[color:var(--control-bg)] p-0 text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)] [&_svg]:block [&_svg]:h-3 [&_svg]:w-3"
              aria-label={expanded ? "Collapse" : "Expand"}
            >
              {expanded ? (
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 12l8-8" /><path d="M12 12L4 4" />
                </svg>
              ) : (
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10 2h4v4" /><path d="M6 14H2v-4" /><path d="M14 2l-4 4" /><path d="M2 14l4-4" />
                </svg>
              )}
            </button>
          ) : null}
          </div>
        );
        return (
          <DetailHeaderSlotContext.Provider value={headerSlot}>
          <div className="grid h-full min-h-0 min-w-0 overflow-hidden bg-[color:var(--bg)]" style={{ gridTemplateRows: "auto minmax(0,1fr)" }}>
            <div className={traceCanvasOn ? "h-0 overflow-hidden" : "overflow-hidden border-b border-[color:var(--line-soft)]"} inert={traceCanvasOn} data-detail-header-row="">
              {headerByline ? (
                // A trace's header is ONE line for every reading of it: the reading
                // switcher first, where a tab strip sits, with the panel's own words
                // for the trace (its summary, on the readings that do not open with
                // it) taking the room after it; the trace's byline set against the
                // right edge like the metadata it is, fitting as many of its facts as
                // the width allows; the panel controls at the edge. The slot and the
                // byline wrapper are `contents`, so what the panel portals in and the
                // strip itself are the line's flex items and size against each other
                // directly. The eyebrow and title this replaces both said the fixed
                // word the tab already says, and the rows below get the two lines back.
                <div data-detail-header-compact="" className="@container flex min-w-0 items-center gap-[12px] pt-[6px] pb-[6px]">
                  <div data-detail-header-slot="" ref={setHeaderSlot} className="contents" />
                  <div data-detail-header-byline="" className="contents">{headerByline}</div>
                  {controls}
                </div>
              ) : (
                <div className="grid gap-0 pt-3 pb-2 [grid-template-columns:minmax(0,1fr)_auto] items-start">
                  <div>
                    <div className={CONTEXT_EYEBROW_CLASS}>{showCellCoOccurrence ? "Context" : contextEyebrow}</div>
                    <h2 className="m-0 mt-[3px] text-[15px] leading-[1.06] font-medium tracking-[-0.04em]">{headerTitle}</h2>
                  </div>
                  {controls}
                </div>
              )}
            </div>
            <div className="min-h-0 min-w-0">
              {body}
            </div>
          </div>
          </DetailHeaderSlotContext.Provider>
        );
      }
    : rightContent
      ? (props) =>
          rightContent({
            ...props,
            palette,
            traceStats,
            hoveredCluster,
            hoverSource,
            traces,
            clusters,
            onHoverClusterRight: handleHoverClusterRight,
          })
      : undefined;

  return (
    <SplitPage
      onCollapsedChange={onDetailCollapsedChange}
      defaultDetailRatio={0.45}
      focusHeader={focusHeader}
      resetKey={resetKey}
      leftContent={(slot) => (
        influenceDescriptor ? (
          <InfluenceTraceView
            key={influenceDescriptor.key}
            descriptor={influenceDescriptor}
            onOpenTrace={onOpenTrace}
            onScopeNavigate={onInfluenceScopeNavigate}
          />
        ) : (
          <LensHost
            registry={appLenses}
            target={{ scopePath, focusDim, range, view }}
            slot={slot}
            ctx={{
              hero, plotConfig, activityLoading, baselines, range, activityLens,
              onRangeChange, onActivityLensChange,
              onHoverCluster: handleHoverClusterLeft, onHoverCell: handleHoverCellLeft,
              onClickCell, hoveredCell, traces, clusters, palette,
              selectedCluster, selectedTrace, surfaceTitle, surfaceScope, surfaceScopeOverride,
              traceLog, traceStats, sessionLog, sessions, logTab, setLogTab, commandLog, commands, canTracePrompts, promptsWithheldHere, canRawCommands,
              query,
              onSteerScope,
              onOpenDetail: onOpenLensDetail ?? setLensDetail, lensDetail,
              onShowDefinition: onShowSignalDefinition,
              onNavigatePath,
            }}
          />
        )
      )}
      rightHeader={rightHeader}
      rightContent={resolvedRightContent}
      rail={rail}
    />
  );
}
